import React, { useState, useEffect } from "react";
import { api } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { MapPin, Plus, Pencil, Trash2, Save, X, Navigation } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { geocodeAddress } from "@/lib/geocoding";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const LocationsManager = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    postalCode: "",
    country: "DE",
    latitude: null,
    longitude: null,
  });
  const [geocoding, setGeocoding] = useState(false);
  const [mapCenter, setMapCenter] = useState([51.1657, 10.4515]); // Germany center

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await api.getLocations();
      setLocations(response.locations || []);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.addressLine1 || !formData.city || !formData.postalCode) {
      toast({
        title: "Eingabe unvollständig",
        description: "Bitte fülle alle Pflichtfelder aus.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      if (editingId) {
        await api.updateLocation(editingId, formData);
        toast({
          title: "Standort aktualisiert",
          description: "Der Standort wurde erfolgreich aktualisiert.",
        });
      } else {
        await api.createLocation(formData);
        toast({
          title: "Standort erstellt",
          description: "Der Standort wurde erfolgreich erstellt.",
        });
      }
      fetchLocations();
      resetForm();
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Der Standort konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (location) => {
    setEditingId(location.id);
    const lat = location.latitude ? parseFloat(location.latitude) : null;
    const lon = location.longitude ? parseFloat(location.longitude) : null;
    setFormData({
      name: location.name,
      addressLine1: location.addressLine1,
      addressLine2: location.addressLine2 || "",
      city: location.city,
      postalCode: location.postalCode,
      country: location.country,
      latitude: lat,
      longitude: lon,
    });
    if (lat && lon) {
      setMapCenter([lat, lon]);
    }
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Möchtest du diesen Standort wirklich löschen?")) return;

    try {
      await api.deleteLocation(id);
      toast({
        title: "Standort gelöscht",
        description: "Der Standort wurde erfolgreich gelöscht.",
      });
      fetchLocations();
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Der Standort konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      postalCode: "",
      country: "DE",
      latitude: null,
      longitude: null,
    });
    setEditingId(null);
    setShowAddForm(false);
    setMapCenter([51.1657, 10.4515]);
  };

  const handleGeocode = async () => {
    if (!formData.city || !formData.postalCode || !formData.addressLine1) {
      toast({
        title: "Unvollständige Adresse",
        description: "Bitte fülle mindestens Straße, PLZ und Stadt aus.",
        variant: "destructive",
      });
      return;
    }

    try {
      setGeocoding(true);
      const result = await geocodeAddress(formData);
      
      if (result) {
        setFormData({
          ...formData,
          latitude: result.lat,
          longitude: result.lon,
        });
        setMapCenter([result.lat, result.lon]);
        toast({
          title: "Koordinaten gefunden",
          description: "Standort wurde erfolgreich lokalisiert.",
        });
      } else {
        toast({
          title: "Adresse nicht gefunden",
          description: "Bitte überprüfe die Adresseingabe.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Geocoding-Fehler",
        description: "Die Adresse konnte nicht lokalisiert werden.",
        variant: "destructive",
      });
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <MapPin className="w-8 h-8" />
              Standorte
            </h1>
            <p className="text-muted-foreground mt-2">
              Verwalte deine Geschäftsstandorte für Aktionen
            </p>
          </div>
          {!showAddForm && (
            <Button onClick={() => setShowAddForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Standort hinzufügen
            </Button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-background rounded-lg border p-6 mb-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {editingId ? "Standort bearbeiten" : "Neuer Standort"}
              </h2>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Hauptfiliale, Filiale Nord"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="addressLine1">
                  Adresse <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="addressLine1"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  placeholder="Straße und Hausnummer"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="addressLine2">Adresszusatz (optional)</Label>
                <Input
                  id="addressLine2"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  placeholder="z.B. 2. Stock, Hintereingang"
                />
              </div>

              <div>
                <Label htmlFor="postalCode">
                  PLZ <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder="12345"
                />
              </div>

              <div>
                <Label htmlFor="city">
                  Stadt <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Berlin"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="country">Land</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="DE"
                />
              </div>
            </div>

            {/* Geocoding & Map */}
            <div className="border-t pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Koordinaten</Label>
                  <p className="text-sm text-muted-foreground">
                    {formData.latitude && formData.longitude ? (
                      <span className="text-green-600 font-mono">
                        {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                      </span>
                    ) : (
                      "Noch nicht lokalisiert"
                    )}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGeocode}
                  disabled={geocoding}
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  {geocoding ? "Lokalisiere..." : "Adresse lokalisieren"}
                </Button>
              </div>

              {/* Map */}
              <div className="h-64 rounded-lg overflow-hidden border">
                <MapContainer
                  center={mapCenter}
                  zoom={formData.latitude && formData.longitude ? 15 : 6}
                  style={{ height: "100%", width: "100%" }}
                  key={`${mapCenter[0]}-${mapCenter[1]}`}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {formData.latitude && formData.longitude && (
                    <Marker position={[formData.latitude, formData.longitude]}>
                      <Popup>
                        <strong>{formData.name || "Neuer Standort"}</strong>
                        <br />
                        {formData.addressLine1}
                        <br />
                        {formData.postalCode} {formData.city}
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={resetForm}>
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                {loading ? "Speichert..." : editingId ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </div>
        )}

        {/* Locations List */}
        <div className="space-y-4">
          {locations.length === 0 ? (
            <div className="bg-background rounded-lg border p-12 text-center">
              <MapPin className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Noch keine Standorte
              </h3>
              <p className="text-muted-foreground mb-6">
                Füge deinen ersten Standort hinzu, um ihn in Aktionen verwenden zu können.
              </p>
              {!showAddForm && (
                <Button onClick={() => setShowAddForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ersten Standort hinzufügen
                </Button>
              )}
            </div>
          ) : (
            locations.map((location) => (
              <div
                key={location.id}
                className="bg-background rounded-lg border p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      <h3 className="text-xl font-semibold">{location.name}</h3>
                    </div>
                    <div className="text-muted-foreground space-y-1">
                      <p>{location.addressLine1}</p>
                      {location.addressLine2 && <p>{location.addressLine2}</p>}
                      <p>
                        {location.postalCode} {location.city}
                      </p>
                      <p className="text-sm text-muted-foreground">{location.country}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(location)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(location.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationsManager;
